if (!customElements.get('compact-product-bundle')) {
  customElements.define(
    'compact-product-bundle',
    class CompactProductBundle extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        if (this.submitButton) {
          this.on(this.submitButton, 'click', this.onSubmitHandler.bind(this));
          this.selectedVariants = this.selectedVariants || new WeakMap();
          this.productForms.forEach((form) => {
            this.on(form, 'variant:change', (event) => {
              this.selectedVariants.set(form, event.detail?.variant || null);
              this.serverUnavailable = false;
              this.updateSubmitAvailability();
            });
            this.on(form, 'variant:unavailable', () => {
              this.selectedVariants.set(form, null);
              this.serverUnavailable = false;
              this.updateSubmitAvailability();
            });
          });
          this.updateSubmitAvailability();
        }
      }
    
      get productForms() {
        return Array.from(this.querySelectorAll('form[is="product-bundle-form"]'));
      }

      get variants() {
        return this.productForms.map((form) => form.querySelector('[name="id"]'));
      }
    
      get submitButton() {
        return this.querySelector('[data-product-bundle-submit]');
      }
    
      get cartDrawer() {
        return document.querySelector('cart-drawer');
      }

      areAllProductsAvailable() {
        if (this.productForms.length === 0) return false;

        return this.productForms.every((form) => {
          const variantInput = form.querySelector('[name="id"]');
          const productSubmitButton = form.querySelector('button[name="add"]');
          if (!variantInput || !productSubmitButton) return false;

          let selectedVariant = this.selectedVariants?.get(form);
          if (!this.selectedVariants?.has(form)) {
            const selectedVariantElement = form.querySelector('[data-selected-variant]');
            if (!selectedVariantElement) return false;
            try {
              selectedVariant = JSON.parse(selectedVariantElement.textContent);
            } catch {
              return false;
            }
          }

          if (
            selectedVariant?.available !== true ||
            String(selectedVariant.id) !== String(variantInput.value) ||
            productSubmitButton.disabled
          ) return false;

          const minimumQuantity = Number(selectedVariant.quantity_rule?.min ?? variantInput.getAttribute('data-min') ?? 1);
          if (!Number.isFinite(minimumQuantity) || minimumQuantity < 1) return false;

          const inventoryManagement = selectedVariant.inventory_management;
          const inventoryPolicy = selectedVariant.inventory_policy;
          if (minimumQuantity > 1 && inventoryManagement === undefined) return false;
          if (minimumQuantity > 1 && inventoryManagement === 'shopify' && inventoryPolicy !== 'continue') {
            const inventoryQuantity = Number(selectedVariant.inventory_quantity);
            if (!Number.isFinite(inventoryQuantity) || minimumQuantity > inventoryQuantity) return false;
          }

          return true;
        });
      }

      updateSubmitAvailability() {
        if (!this.submitButton) return false;

        const available = !this.serverUnavailable && this.areAllProductsAvailable();
        this.submitButton.disabled = !available;
        this.submitButton.setAttribute('aria-disabled', String(!available));
        this.submitButton.setAttribute(
          'aria-label',
          available
            ? this.submitButton.dataset.availableAriaLabel
            : this.submitButton.dataset.unavailableAriaLabel
        );

        const submitButtonText = this.submitButton.querySelector('.btn-text');
        if (submitButtonText) {
          submitButtonText.textContent = available
            ? this.submitButton.dataset.availableLabel
            : this.submitButton.dataset.unavailableLabel;
        }

        if (available) this.handleErrorMessage();
        return available;
      }

      handleCartError(parsedState) {
        const errorMessage = parsedState.description || '';
        this.handleErrorMessage(errorMessage);
        document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
          detail: {
            errorMessage
          }
        }));

        if (/sold[\s-]?out|not available|unavailable/i.test(errorMessage)) {
          this.serverUnavailable = true;
          this.error = true;
          this.updateSubmitAvailability();
          this.handleErrorMessage(errorMessage);
        }
      }
    
      onSubmitHandler(event) {
        if (!this.updateSubmitAvailability()) {
          event.preventDefault();
          this.handleErrorMessage(this.submitButton?.dataset.unavailableLabel || '');
          return;
        }

        const data = {
          items: this.variants.map(variant => ({
            id: variant.value,
            quantity: variant.getAttribute('data-min')
          }))
        };
    
        if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
          theme.utils.postLink2(theme.routes.cart_add_url, {
            parameters: {
              ...data
            }
          });
          return;
        }
        
        event.preventDefault();
        if (this.submitButton.hasAttribute('aria-busy')) return;
        this.error = false;
        this.activeElement = event.submitter || event.currentTarget;
    
        this.handleErrorMessage();
    
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
        
        const body = JSON.stringify({
          ...data,
          sections: sectionsToBundle,
          sections_url: window.location.pathname
        });
    
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.setAttribute('aria-busy', 'true');
    
        fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.handleCartError(parsedState);
              return;
            }
    
            const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
            cartJson['sections'] = parsedState['sections'];
    
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle', cart: cartJson });
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
              detail: {
                product: parsedState
              }
            }));
    
            this.cartDrawer?.show(this.activeElement);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.submitButton.removeAttribute('aria-busy');
    
            if (!this.error && !this.serverUnavailable) {
              this.submitButton.removeAttribute('aria-disabled');
            }
          });
      }
    
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        
        this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
        if (!this.errorMessage) return;
    
        this.errorMessage.toggleAttribute('hidden', !errorMessage);
        this.errorMessage.innerText = errorMessage;
      }
    }
  );
}
